import React, { useState, useEffect, useCallback } from 'react';
import { CURRENCY_META } from '../../data/currencyMeta';

const API_URL = 'https://cdn.moneyconvert.net/api/latest.json';

interface ApiResponse {
  base: string;
  rates: Record<string, number>;
  ts?: string;
}

/** Fiat currencies only - exclude crypto/precious metals/non-standard */
const EXCLUDE_CODES = new Set([
  'BTC', 'ETH', 'DOGE', 'XAU', 'XAG', 'XPD', 'XPT', 'ASPAR', 'DOLARBLUE',
  'DZDBM', 'EGPBM', 'NGNBM', 'PMRRUB', 'UAHBM', 'WAUA', 'LD', 'SPL',
  'CNH', 'CLF', 'CUC', 'GGP', 'IMP', 'JEP', 'KPW', 'TVD', 'VED', 'ZWG',
]);

/** All world currencies (from CURRENCY_META) for the dropdown. */
const ALL_CURRENCY_CODES = Object.keys(CURRENCY_META)
  .filter((c) => !EXCLUDE_CODES.has(c))
  .sort((a, b) => a.localeCompare(b));

function matchCurrency(code: string, query: string, meta: { name: string; country: string } | undefined): boolean {
  const q = query.toLowerCase().trim();
  if (!q) return true;
  if (code.toLowerCase().includes(q)) return true;
  if (meta?.name.toLowerCase().includes(q)) return true;
  if (meta?.country.toLowerCase().includes(q)) return true;
  return false;
}

interface SearchableCurrencySelectProps {
  value: string;
  onChange: (code: string) => void;
  availableCodes: string[];
  label: string;
}

const SearchableCurrencySelect: React.FC<SearchableCurrencySelectProps> = ({
  value,
  onChange,
  availableCodes,
  label,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = React.useRef<HTMLInputElement>(null);

  const filtered = query.trim()
    ? availableCodes.filter((c) => matchCurrency(c, query, CURRENCY_META[c]))
    : availableCodes;

  const selectedMeta = CURRENCY_META[value];

  useEffect(() => {
    if (isOpen) setQuery('');
  }, [isOpen]);

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 bg-white text-left flex items-center justify-between"
      >
        <span>
          {value}
          {selectedMeta && (
            <span className="text-gray-500 font-normal ml-1">— {selectedMeta.name}</span>
          )}
        </span>
        <svg
          className={`w-5 h-5 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />
          <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
            <div className="p-2 border-b border-gray-100">
              <input
                ref={inputRef}
                type="text"
                placeholder="Search by currency name or country..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
                autoFocus
              />
            </div>
            <div className="max-h-60 overflow-y-auto">
              {filtered.length === 0 ? (
                <p className="px-4 py-3 text-sm text-gray-500">No currencies match your search</p>
              ) : (
                filtered.map((code) => {
                  const meta = CURRENCY_META[code];
                  const isSelected = code === value;
                  return (
                    <button
                      key={code}
                      type="button"
                      onClick={() => {
                        onChange(code);
                        setIsOpen(false);
                      }}
                      className={`w-full px-4 py-2.5 text-left text-sm hover:bg-orange-50 flex items-center gap-2 ${
                        isSelected ? 'bg-orange-50 text-orange-700 font-medium' : 'text-gray-800'
                      }`}
                    >
                      {isSelected && (
                        <span className="text-orange-600 font-bold">✓</span>
                      )}
                      <span className="font-medium">{code}</span>
                      {meta && (
                        <span className="text-gray-500 truncate">
                          — {meta.name} ({meta.country})
                        </span>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

/** Parse programmatic SEO slug e.g. "usd-to-inr" → { from: "USD", to: "INR" } */
function parseCurrencySlug(
  slug: string | undefined,
  availableCodes: string[]
): { from: string; to: string } | null {
  if (!slug) return null;
  const match = slug.toLowerCase().match(/^([a-z]{3})-to-([a-z]{3})$/);
  if (!match) return null;
  const fromC = match[1].toUpperCase();
  const toC = match[2].toUpperCase();
  if (availableCodes.includes(fromC) && availableCodes.includes(toC)) {
    return { from: fromC, to: toC };
  }
  return null;
}

interface CurrencyConverterToolProps {
  slug?: string;
}

const CurrencyConverterTool: React.FC<CurrencyConverterToolProps> = ({ slug }) => {
  const [amount, setAmount] = useState('100');
  const [from, setFrom] = useState('USD');
  const [to, setTo] = useState('INR');
  const [rates, setRates] = useState<Record<string, number>>({});
  const [ratesDate, setRatesDate] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(API_URL);
      if (!res.ok) throw new Error('Failed to fetch rates');
      const data: ApiResponse = await res.json();
      setRates(data.rates);
      if (data.ts) {
        const d = new Date(data.ts);
        setRatesDate(d.toISOString().slice(0, 10));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load exchange rates');
      setRates({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRates();
  }, [fetchRates]);

  useEffect(() => {
    const p = parseCurrencySlug(slug, ALL_CURRENCY_CODES);
    if (p) {
      setFrom(p.from);
      setTo(p.to);
    }
  }, [slug]);

  const num = parseFloat(amount) || 0;
  const rateFromUsd = from === 'USD' ? 1 : rates[from] ?? 0;
  const rateToUsd = to === 'USD' ? 1 : rates[to] ?? 0;
  const rateToTarget = rateFromUsd && rateToUsd ? rateToUsd / rateFromUsd : 0;
  const result = num * rateToTarget;

  const inputClass =
    'w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400';

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
        <input
          type="number"
          min={0}
          step="any"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="0"
          className={inputClass}
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <SearchableCurrencySelect
          label="From"
          value={from}
          onChange={setFrom}
          availableCodes={ALL_CURRENCY_CODES}
        />
        <SearchableCurrencySelect
          label="To"
          value={to}
          onChange={setTo}
          availableCodes={ALL_CURRENCY_CODES}
        />
      </div>

      {loading && (
        <div className="text-sm text-gray-500 flex items-center gap-2">
          <span className="inline-block w-4 h-4 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
          Loading latest rates…
        </div>
      )}

      {error && (
        <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
          {error}. Check your connection or try again later.
        </div>
      )}

      <div className="bg-orange-50 rounded-xl p-6 border border-orange-100">
        <div className="text-2xl font-bold text-orange-700">
          {loading ? '—' : rateToTarget > 0 ? result.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'} {to}
        </div>
        {!loading && rateToTarget === 0 && (
          <p className="text-sm text-amber-700 mt-2">Live rates not available for this pair. Try another currency.</p>
        )}
        {ratesDate && !loading && rateToTarget > 0 && (
          <>
            <p className="text-sm text-gray-700 mt-3">
              Today’s rate:{' '}
              <span className="font-semibold">
                1 {from} = {rateToTarget.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 })} {to}
              </span>
            </p>
            <p className="text-xs text-gray-500 mt-1">Live rates · as of {ratesDate}</p>
          </>
        )}
      </div>
    </div>
  );
};

export default CurrencyConverterTool;
